import ErrorFallback from "@/components/ErrorFallback";
import ErrorMessage from "@/components/ErrorMessage";
import Title from "@/components/Title";
import { PASSWORD_SYMBOL_REGEX } from "@/components/activation/Constants";
import { SignInState } from "@/components/auth/Types";
import {
  LOGIN_PASSWORD_VALIDATORS,
  NEW_PASSWORD_CONFIRM_VALIDATORS,
  NEW_PASSWORD_WITHOUT_CHECKLIST_VALIDATORS,
  Validator,
} from "@/components/auth/ValidatableTextField";
import { BackdropIndicator } from "@/components/common/BackdropIndicator";
import { SCLink } from "@/components/common/SCLink";
import { OrgDashboardLabel } from "@/components/org/OrgDashboardLabel";
import { OrgValidatableTextField } from "@/components/org/OrgValidatableTextField";
import { SCAppBar } from "@/components/sleep_checkup_v1/SCAppBar";
import { PasswordCheckListItem } from "@/pages/examinee/activation/SignUp";
import { facilityTheme } from "@/theme/themeV1";
import {
  callOrgLoginAPI,
  callOrgSetNewPasswordAPI,
  callOrgVerifyCodeAPI,
  callOrgVerifyPinAPI,
  SIGNIN_TYPE,
} from "@/utils/auth";
import { getAxios, getOrgApiServerUrl } from "@/utils/axios";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import {
  Alert,
  Box,
  Button,
  Stack,
  ThemeProvider,
  Typography,
} from "@mui/material";
import { createBrowserHistory } from "history";
import { FormEvent, useCallback, useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useLocation, useNavigate } from "react-router-dom";

const USERNAME_VALIDATORS: Validator[] = [
  (value: string) => (value === "" ? "ユーザー名は必須です" : ""),
];

const CONFIRMATION_CODE_VALIDATORS: Validator[] = [
  (value: string) => (value === "" ? "確認コードを入力してください。" : ""),
  (value: string) =>
    !value.match(/^([0-9]{6})$/) ? "半角数字6桁で入力してください。" : "",
];

const PIN_CODE_VALIDATORS: Validator[] = [
  (value: string) => (value === "" ? "PINコードは必須です" : ""),
];

function OrgSignIn() {
  const MIN_WIDTH = 1098;
  const navigate = useNavigate();
  const location = useLocation();
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [signInType, setSignInType] = useState<SIGNIN_TYPE>("init");
  const [showIndicator, setShowIndicator] = useState(false);
  const [sessionToken, setSessionToken] = useState<string>("");
  const [loginUsername, setLoginUsername] = useState<string>("");
  const signInState = location.state as SignInState | null | undefined;
  const query = new URLSearchParams(location.search);
  const next = query.get("next") ?? "/org/projects";

  const clearHistory = useCallback(
    (e: any) => {
      e.preventDefault();
      const customHistory = createBrowserHistory();
      if (customHistory.location.state) {
        navigate(location.pathname, {});
      }
    },
    [location, navigate]
  );
  useEffect(() => {
    window.addEventListener("beforeunload", clearHistory);
    return () => {
      window.removeEventListener("beforeunload", clearHistory);
    };
  }, [clearHistory]);

  const handleSessionError = (errorMessages: string[]) => {
    const msg = errorMessages[0];
    setErrorMessage(msg);
    if (msg.includes("セッションが無効です")) {
      setSignInType("init");
    }
  };

  const LoginForm = () => {
    const [usernameValid, setUsernameValid] = useState(false);
    const [passwordValid, setPasswordValid] = useState(false);

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setErrorMessage("");
      location.state = "";
      const data = new FormData(event.currentTarget);
      const username = data.get("username")?.toString() ?? "";
      const password = data.get("password")?.toString() ?? "";

      try {
        setShowIndicator(true);
        const result = await callOrgLoginAPI(
          getAxios(getOrgApiServerUrl()),
          username,
          password
        );
        setSignInType(result.signInType);
        if (result.sessionToken) {
          setSessionToken(result.sessionToken);
          setLoginUsername(username);
        }
        if (result.signInType === "success") {
          navigate(next);
        }
      } catch (e: any) {
        const res = e.response?.data;
        if (res?.error_messages && res.error_messages.length > 0) {
          setErrorMessage(res.error_messages[0]);
        } else {
          setErrorMessage("ユーザー名またはパスワードが違います。");
        }
        setSignInType("init");
      } finally {
        setShowIndicator(false);
      }
    };

    return (
      <Stack
        component="form"
        onSubmit={handleSubmit}
        spacing="40px"
        alignItems="flex-start"
        sx={{ flex: "none", order: "0", flexGrow: "0" }}
      >
        <Typography variant="h3" color="text.primary">
          SLEEP COMPASS ダッシュボード
        </Typography>
        <Stack spacing="24px" alignItems="flex-start" sx={{ width: "432px" }}>
          <OrgValidatableTextField
            validators={USERNAME_VALIDATORS}
            id="username"
            label="ユーザー名"
            name="username"
            autoComplete="username"
            autoFocus
            sx={{ gap: 0 }}
            onChangeErrorMessage={(msg) => setUsernameValid(msg === "")}
          />
          <OrgValidatableTextField
            validators={LOGIN_PASSWORD_VALIDATORS}
            name="password"
            label="パスワード"
            type="password"
            id="password"
            autoComplete="current-password"
            sx={{ gap: 0 }}
            onChangeErrorMessage={(msg) => setPasswordValid(msg === "")}
          />
        </Stack>
        <Stack
          spacing="24px"
          alignItems="flex-start"
          sx={{
            width: "432px",
            height: "90px",
            flex: "none",
            order: "2",
            flexGrow: "0",
          }}
        >
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={!(usernameValid && passwordValid)}
          >
            ログイン
          </Button>
          <SCLink
            variant="caption"
            to="/org/forgot_password"
            sx={{ color: "#3378B3" }}
          >
            パスワードを忘れた場合
          </SCLink>
        </Stack>
      </Stack>
    );
  };

  const VerifyPinForm = () => {
    const [pinCodeValid, setPinCodeValid] = useState(false);

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setErrorMessage("");
      const data = new FormData(event.currentTarget);
      const pinCode = data.get("pinCode")?.toString() ?? "";

      try {
        setShowIndicator(true);
        await callOrgVerifyPinAPI(
          getAxios(getOrgApiServerUrl()),
          sessionToken,
          loginUsername,
          pinCode
        );
        setSignInType("setNewPassword");
      } catch (e: any) {
        const res = e.response?.data;
        if (res?.error_messages && res.error_messages.length > 0) {
          handleSessionError(res.error_messages);
        } else {
          setErrorMessage(
            "エラーが発生しました。しばらくしてから再度お試しください。"
          );
        }
      } finally {
        setShowIndicator(false);
      }
    };

    return (
      <Stack
        component="form"
        onSubmit={handleSubmit}
        spacing="40px"
        alignItems="flex-start"
        sx={{ flex: "none", order: "0", flexGrow: "0" }}
      >
        <Stack spacing="16px" alignItems="flex-start">
          <Typography variant="h3" color="text.primary">
            PINコードによる確認
          </Typography>
          <Typography variant="body2">
            予め登録したPINコードを入力してください。
          </Typography>
        </Stack>
        <Stack spacing="24px" alignItems="flex-start" sx={{ width: "432px" }}>
          <OrgValidatableTextField
            validators={PIN_CODE_VALIDATORS}
            id="pinCode"
            label="PINコード"
            name="pinCode"
            autoComplete="off"
            autoFocus
            onChangeErrorMessage={(msg) => setPinCodeValid(msg === "")}
          />
          <Typography variant="caption" color="text.secondary">
            忘れてしまった場合は、ACCELStarsの担当者までお問い合わせください。
          </Typography>
        </Stack>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={!pinCodeValid}
        >
          確認する
        </Button>
      </Stack>
    );
  };

  const VerifyCodeForm = () => {
    const [codeValid, setCodeValid] = useState(false);

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setErrorMessage("");
      const data = new FormData(event.currentTarget);
      const confirmationCode = data.get("confirmationCode")?.toString() ?? "";

      try {
        setShowIndicator(true);
        await callOrgVerifyCodeAPI(
          getAxios(getOrgApiServerUrl()),
          sessionToken,
          confirmationCode
        );
        navigate(next);
      } catch (e: any) {
        const res = e.response?.data;
        if (res?.error_messages && res.error_messages.length > 0) {
          handleSessionError(res.error_messages);
        } else {
          setErrorMessage(
            "エラーが発生しました。しばらくしてから再度お試しください。"
          );
        }
      } finally {
        setShowIndicator(false);
      }
    };

    return (
      <Stack
        component="form"
        onSubmit={handleSubmit}
        spacing="40px"
        alignItems="flex-start"
        sx={{ flex: "none", order: "0", flexGrow: "0" }}
      >
        <Stack spacing="16px" alignItems="flex-start">
          <Typography variant="h3" color="#282828">
            確認コードによる2段階認証
          </Typography>
          <Typography variant="body2">
            ログインの安全性を確認するため、予め登録されたメールアドレスにお送りした6桁の確認コードを入力してください。
          </Typography>
        </Stack>
        <Stack spacing="24px" alignItems="flex-start" sx={{ width: "432px" }}>
          <OrgValidatableTextField
            validators={CONFIRMATION_CODE_VALIDATORS}
            validateOnBlur
            id="confirmationCode"
            label="確認コード（6桁）"
            name="confirmationCode"
            autoComplete="one-time-code"
            autoFocus
            onChangeErrorMessage={(msg) => setCodeValid(msg === "")}
          />
          <Typography
            variant="caption"
            sx={{ fontSize: 12, fontWeight: 400, color: "#7A7A7A" }}
          >
            確認コードが届かない場合は、登録されたメールアドレスが間違っている可能性があります。施設のシステム管理者までお問い合わせください。
          </Typography>
        </Stack>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={!codeValid}
        >
          確認する
        </Button>
      </Stack>
    );
  };

  const SetNewPasswordForm = () => {
    const [newPasswordSubmittable, setNewPasswordSubmittable] = useState(false);
    const [reNewPasswordSubmittable, setReNewPasswordSubmittable] =
      useState(false);
    const [password, setPassword] = useState("");

    const validators = {
      passwordLength: () => {
        if (password.length < 8 || 24 < password.length) {
          return ["NG"];
        }
        return null;
      },
      passwordAlphabetLowerAndUpper: () => {
        if (!(password.match(/[a-z]/) && password.match(/[A-Z]/))) {
          return ["NG"];
        }
        return null;
      },
      passwordDigit: () => {
        if (!password.match(/\d/)) {
          return ["NG"];
        }
        return null;
      },
      passwordSymbol: () => {
        if (!password.match(PASSWORD_SYMBOL_REGEX)) {
          return ["NG"];
        }
        return null;
      },
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setErrorMessage("");
      const data = new FormData(event.currentTarget);
      const newPassword = data.get("newPassword")?.toString() ?? "";
      const reEnterPassword = data.get("reEnterPassword")?.toString() ?? "";

      if (newPassword !== reEnterPassword) {
        setErrorMessage("パスワードが一致しません。");
        return;
      }

      try {
        setShowIndicator(true);
        const result = await callOrgSetNewPasswordAPI(
          getAxios(getOrgApiServerUrl()),
          sessionToken,
          loginUsername,
          newPassword
        );
        if (result === "success") {
          navigate("/org/projects", { replace: true });
        } else {
          navigate("/org/signin", {
            replace: true,
            state: {
              message: "パスワードの設定が完了しました。ログインしてください。",
            },
          });
        }
      } catch (e: any) {
        const res = e.response?.data;
        if (res?.error_messages && res.error_messages.length > 0) {
          handleSessionError(res.error_messages);
        } else {
          setErrorMessage(
            "エラーが発生しました。しばらくしてから再度お試しください。"
          );
        }
      } finally {
        setShowIndicator(false);
      }
    };

    return (
      <Stack
        component="form"
        onSubmit={handleSubmit}
        spacing="40px"
        alignItems="flex-start"
        sx={{ flex: "none", order: "0", flexGrow: "0" }}
      >
        <Stack spacing="16px" alignItems="flex-start">
          <Typography variant="h3" color="#282828">
            新しいパスワードを設定しましょう
          </Typography>
          <Typography variant="body2" color="#282828">
            パスワードは大文字、小文字を含む半角英字、数字、記号を組み合わせた8文字以上24文字以下で入力してください
          </Typography>
        </Stack>
        <Stack spacing="24px">
          <Stack spacing="4px" alignItems="flex-start" sx={{ width: "432px" }}>
            <OrgValidatableTextField
              validators={NEW_PASSWORD_WITHOUT_CHECKLIST_VALIDATORS}
              margin="normal"
              name="newPassword"
              label="新しいパスワード"
              type="password"
              id="newPassword"
              autoComplete="new-password"
              autoFocus
              onChangeErrorMessage={(msg) =>
                setNewPasswordSubmittable(
                  msg === "" &&
                    !validators.passwordLength() &&
                    !validators.passwordAlphabetLowerAndUpper() &&
                    !validators.passwordDigit() &&
                    !validators.passwordSymbol()
                )
              }
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 1 }}
            />
            <Stack spacing="4px" sx={{ pl: "13px" }}>
              <PasswordCheckListItem
                text="8文字以上24文字以下の長さにしてください"
                validator={validators.passwordLength}
              />
              <PasswordCheckListItem
                text="大文字と小文字の半角英字を含めてください"
                validator={validators.passwordAlphabetLowerAndUpper}
              />
              <PasswordCheckListItem
                text="半角数字を含めてください"
                validator={validators.passwordDigit}
              />
              <PasswordCheckListItem
                text="記号を含めてください"
                validator={validators.passwordSymbol}
              />
            </Stack>
          </Stack>
          <OrgValidatableTextField
            validators={NEW_PASSWORD_CONFIRM_VALIDATORS}
            margin="normal"
            name="reEnterPassword"
            label="新しいパスワード（確認）"
            type="password"
            id="reEnterNewPassword"
            autoComplete="new-password"
            onChangeErrorMessage={(msg) =>
              setReNewPasswordSubmittable(msg === "")
            }
          />
        </Stack>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={!(newPasswordSubmittable && reNewPasswordSubmittable)}
        >
          設定する
        </Button>
      </Stack>
    );
  };

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <ThemeProvider theme={facilityTheme}>
        <>
          <Title title="SLEEP COMPASS" />
          <meta
            name="description"
            content="睡眠健康測定 SLEEP COMPASSの組織用ダッシュボード"
          />
        </>
        <SCAppBar toolbarItem={<OrgDashboardLabel />} minWidth={MIN_WIDTH} />
        <BackdropIndicator open={showIndicator} />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "80px 0",
            position: "absolute",
            width: "100%",
            left: "0px",
            top: "72px",
          }}
        >
          <Stack spacing="40px" sx={{ width: "432px" }}>
            {signInState?.message != null && (
              <Alert
                icon={
                  <TaskAltIcon
                    sx={{ color: "success.main", alignSelf: "center" }}
                  />
                }
                sx={{
                  backgroundColor: "#E6F6F1",
                  color: "success.main",
                }}
              >
                {signInState?.message}
              </Alert>
            )}
            <ErrorMessage
              errorMessage={errorMessage ?? ""}
              sx={{ color: "error.main" }}
            />
            {signInType === "init" && <LoginForm />}
            {signInType === "verifyCode" && <VerifyCodeForm />}
            {signInType === "newPasswordRequired" && <VerifyPinForm />}
            {signInType === "setNewPassword" && <SetNewPasswordForm />}
          </Stack>
        </Box>
        <Box
          component="footer"
          sx={{
            py: 3,
            px: 2,
            mt: "auto",
          }}
        ></Box>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default OrgSignIn;
